import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Calendar, User, Phone, CheckCircle, Clock, 
  ChevronLeft, ChevronRight, Maximize2, X, XCircle, Share2, Printer, 
  HelpCircle, AlertTriangle, ShieldCheck, Mail, Users, FileText,
  ExternalLink, Sparkles, Check, Play, Pause, Bookmark,
  ChevronDown, ChevronUp, Calculator, Info, GitBranch, ArrowRight, CornerDownRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import '../styles/erp-guide.css';

const STEPS_DATA = [
  {
    step: 1,
    title: 'Mở Menu Tài Khoản & Chọn "Xin nghỉ phép"',
    shortTitle: 'Bước 1: Mở Menu',
    badgeText: 'Thao tác nhanh • 10 giây',
    image: '/images/huong-dan/tao-nghi-phep/step1-menu-xin-nghi-phep.png',
    caption: 'Giao diện Header ERP FIT Tour: Nhấp vào Avatar góc trên bên phải để mở Menu tài khoản',
    desc: 'Trên bất kỳ màn hình nào của ERP FIT Tour, bạn đều có thể mở nhanh form xin nghỉ phép chỉ với 2 lần nhấp chuột.',
    actions: [
      {
        icon: '1',
        title: 'Nhấp vào ảnh đại diện (Avatar)',
        detail: 'Tại góc trên bên phải thanh điều hướng Header, nhấp vào ảnh đại diện hoặc tên tài khoản của bạn.'
      },
      {
        icon: '2',
        title: 'Chọn mục "🌴 Xin nghỉ phép"',
        detail: 'Menu thả xuống sẽ hiển thị danh sách tiện ích cá nhân. Nhấp chọn dòng chữ màu tím nổi bật mang tên "🌴 Xin nghỉ phép".'
      },
      {
        icon: '3',
        title: 'Màn hình hiển thị form tạo đơn',
        detail: 'Ngay lập tức, cửa sổ pop-up "Tạo Đơn Xin Nghỉ Phép" sẽ xuất hiện trên màn hình mà không cần chuyển trang.'
      }
    ],
    tip: 'Mẹo: Bạn có thể mở form này từ bất kỳ đâu (Dashboard, Lead Marketing, Báo cáo...) mà không làm gián đoạn công việc đang dở dang.',
    highlightTag: 'Menu Profile > 🌴 Xin nghỉ phép'
  },
  {
    step: 2,
    title: 'Điền Thông Tin Cơ Bản & Chọn Ngày Nghỉ Trên Lịch',
    shortTitle: 'Bước 2: Chọn Ngày & Phép',
    badgeText: 'Cơ chế 2026 • Tự động trừ T7, CN',
    image: '/images/huong-dan/tao-nghi-phep/step2-thong-tin-va-chon-ngay.png',
    caption: 'Giao diện chọn ngày nghỉ trên lịch tương tác, hiển thị quỹ phép dư và đối soát âm phép',
    desc: 'Thiết lập loại phép nghỉ, kiểm tra thông tin liên lạc và chọn các ngày cần nghỉ trên bộ lịch thông minh.',
    actions: [
      {
        icon: '1',
        title: 'Nhân viên xin phép & Người duyệt',
        detail: 'Mặc định là tài khoản của bạn. Đơn sẽ tự động định tuyến đến Trưởng phòng/BUM trực tiếp và Ban Giám Đốc (BOD: Sếp Max Vũ & Sếp Vy Phan).'
      },
      {
        icon: '2',
        title: 'Chọn Loại nghỉ phép & SĐT liên hệ',
        detail: 'Chọn "Nghỉ phép năm" (trừ quỹ phép, BUM/BOD duyệt) hoặc "Đi công tác / phân công" (Hệ thống Auto-Duyệt ngay lập tức). Điền SĐT khẩn cấp.'
      },
      {
        icon: '3',
        title: 'Nhấp chọn ngày trên Lịch tương tác',
        detail: 'Bấm trực tiếp vào các ngày dự kiến nghỉ. Hệ thống tự động khóa Thứ 7, Chủ Nhật. Hỗ trợ chọn nghỉ cả ngày (1 ngày) hoặc nửa ngày Sáng/Chiều (0.5 ngày).'
      },
      {
        icon: '4',
        title: 'Quỹ Phép Dư & Cảnh Báo Vượt Phép',
        detail: 'Quan sát huy hiệu phép dư. Nếu số ngày xin vượt quá quỹ phép, hệ thống vẫn cho phép gửi nhưng sẽ cảnh báo số ngày âm phép để BUM/BOD xem xét và Kế toán (Nguyễn Xuân) đối soát trừ lương.'
      }
    ],
    tip: 'Lưu ý: Riêng loại "Đi công tác / Phân công theo công việc", hệ thống sẽ Tự Động Duyệt ngay khi bấm gửi mà không cần chờ duyệt.',
    highlightTag: 'Lịch tự động trừ Thứ 7 & CN'
  },
  {
    step: 3,
    title: 'Nhập Lý Do, Bàn Giao Công Việc & Gửi Duyệt',
    shortTitle: 'Bước 3: Bàn Giao & Gửi Đơn',
    badgeText: 'Duyệt 2 cấp BUM & BOD • Broadcast ALL_STAFF',
    image: '/images/huong-dan/tao-nghi-phep/step3-ly-do-va-ban-giao.png',
    caption: 'Nhập lý do đầy đủ, người nhận bàn giao và hệ thống tự động gửi email theo luồng chuẩn',
    desc: 'Bàn giao công việc dở dang, đảm bảo tiến độ phòng ban không gián đoạn và kích hoạt luồng duyệt email 2 cấp.',
    actions: [
      {
        icon: '1',
        title: 'Trình bày Lý do xin nghỉ (*)',
        detail: 'Nhập lý do nghỉ phép đầy đủ, rõ ràng và trung thực. Lưu ý: Lý do này sẽ được hiển thị công khai trong email thông báo Toàn bộ Nhân viên (ALL_STAFF) khi đơn được duyệt.'
      },
      {
        icon: '2',
        title: 'Bàn giao công việc cho đồng nghiệp',
        detail: 'Tại ô "Người nhận bàn giao", chọn đồng nghiệp sẽ thay bạn hỗ trợ khách hàng/task. Ghi chú rõ các mã booking, hợp đồng quan trọng.'
      },
      {
        icon: '3',
        title: 'Bấm nút "Gửi Đơn Xin Nghỉ Phép"',
        detail: 'Email thông báo kèm bảng đối chiếu (lịch nghỉ, số ngày phép còn lại) sẽ lập tức gửi đến BUM và BOD (Sếp Max Vũ, Sếp Vy Phan).'
      },
      {
        icon: '4',
        title: 'Nhận kết quả duyệt qua Email',
        detail: 'Khi được duyệt: Hệ thống gửi email xác nhận cho bạn và bắn email lịch nghỉ (kèm lý do) cho Toàn bộ Nhân viên. Nếu bị từ chối: Email gửi kèm lý do không duyệt.'
      }
    ],
    tip: 'Bắt buộc: Hãy chủ động trao đổi trước với người nhận bàn giao để đồng nghiệp nắm thông tin trước khi gửi đơn.',
    highlightTag: 'Gửi duyệt BUM/BOD & Bắn Email tự động'
  }
];

const DATA_FIELDS = [
  {
    name: 'Nhân viên xin phép',
    required: true,
    type: 'Dropdown chọn nhân sự',
    desc: 'Mặc định là tài khoản đang đăng nhập. Leader hoặc Admin có thể chọn nhân sự khác nếu làm đơn xin nghỉ hộ.'
  },
  {
    name: 'Cấp duyệt (BUM & BOD)',
    required: true,
    type: 'Ma trận duyệt tự động',
    desc: 'Bắt buộc duyệt 2 cấp: Trưởng phòng/BUM phụ trách trực tiếp (BU1: Trần Quốc Thịnh; BU2 & Visa: Lê Minh Tuấn; BU5: Quỳnh Như; Marketing: Loki; Kế toán: Nguyễn Xuân) và BẮT BUỘC luôn có Ban Giám Đốc (BOD: Sếp Max Vũ & Sếp Vy Phan). Cả BUM hoặc BOD đều có quyền duyệt đơn.'
  },
  {
    name: 'Loại nghỉ phép',
    required: true,
    type: 'Dropdown phân loại',
    desc: 'Gồm: Nghỉ phép năm (trừ quỹ phép, BUM/BOD duyệt); Đi công tác / phân công (Auto-Duyệt ngay lập tức); Nghỉ ốm / thai sản; Nghỉ việc riêng có hưởng lương (hiếu, hỉ); Nghỉ không lương.'
  },
  {
    name: 'Số điện thoại liên hệ',
    required: false,
    type: 'Số điện thoại',
    desc: 'Số điện thoại đang hoạt động của bạn để Ban Giám Đốc hoặc đồng nghiệp liên hệ trong các trường hợp tour hoặc khách hàng phát sinh khẩn cấp.'
  },
  {
    name: 'Chọn ngày nghỉ & Buổi',
    required: true,
    type: 'Lịch tương tác (Interactive DatePicker)',
    desc: 'Click để chọn hoặc bỏ chọn ngày. Hệ thống tự động khóa Thứ 7 và Chủ Nhật. Hỗ trợ chọn nghỉ cả ngày (1 ngày) hoặc nửa ngày Sáng/Chiều (0.5 ngày).'
  },
  {
    name: 'Lý do nghỉ',
    required: true,
    type: 'Văn bản (Tối đa 500 ký tự)',
    desc: 'Trình bày lý do xin nghỉ đầy đủ, rõ ràng và trung thực. Lưu ý: Lý do này sẽ được gửi trực tiếp cho BUM/BOD thẩm định và hiển thị công khai trong Email thông báo Toàn bộ Nhân viên (ALL_STAFF) khi đơn được duyệt.'
  },
  {
    name: 'Người nhận bàn giao',
    required: false,
    type: 'Tìm kiếm nhân sự',
    desc: 'Đồng nghiệp trong cùng BU hoặc cùng khối vận hành sẽ phụ trách chăm sóc khách hàng, trả lời tin nhắn hoặc xử lý hồ sơ tour trong lúc bạn vắng mặt.'
  },
  {
    name: 'Ghi chú bàn giao',
    required: false,
    type: 'Văn bản (Tối đa 500 ký tự)',
    desc: 'Tóm tắt các task đang chạy, mã hợp đồng/booking, link tài liệu hoặc lưu ý đặc biệt dành cho người nhận bàn giao.'
  },
  {
    name: 'Đối tượng nhận Email',
    required: true,
    type: 'Luồng bắn Email tự động',
    desc: 'Khi tạo: BUM & BOD nhận email kèm bảng số phép còn lại và nút duyệt nhanh. Khi duyệt: Tự động gửi email xác nhận cho bạn và bắn email lịch nghỉ (kèm đầy đủ lý do) cho Toàn bộ nhân viên công ty (ALL_STAFF).'
  }
];

const APPROVER_MATRIX = [
  { dept: 'Ban Giám Đốc (BOD)', leader: 'Sếp Max Vũ & Sếp Vy Phan', bod: '⚡ Auto-Duyệt 100%', note: 'Cấp lãnh đạo cao nhất • Auto-Duyệt khi tự làm đơn & Bắn mail ALL_STAFF' },
  { dept: 'BU1', leader: 'Trần Quốc Thịnh (BUM)', bod: 'Max Vũ & Vy Phan', note: 'Kinh doanh & Vận hành BU1 (BUM auto-duyệt trong quỹ phép năm)' },
  { dept: 'BU2 & Visa', leader: 'Lê Minh Tuấn (BUM)', bod: 'Max Vũ & Vy Phan', note: 'Kinh doanh BU2 & Bộ phận Visa (BUM auto-duyệt trong quỹ phép năm)' },
  { dept: 'BU3', leader: 'Phan Hoàng Trúc Vy (BUM / PGĐ)', bod: 'Max Vũ (Giám Đốc)', note: 'Khối MICE & Doanh nghiệp (Sếp Vy Phan phụ trách trực tiếp)' },
  { dept: 'BU4', leader: 'Võ Thị Hồng Trang (Trang - BUM)', bod: 'Max Vũ & Vy Phan', note: 'Kinh doanh & Vận hành BU4 (BUM auto-duyệt trong quỹ phép năm)' },
  { dept: 'BU5', leader: 'Dương Quỳnh Như (BUM)', bod: 'Max Vũ & Vy Phan', note: 'Kinh doanh & Vận hành BU5 (BUM auto-duyệt trong quỹ phép năm)' },
  { dept: 'Marketing', leader: 'Huỳnh Trọng Hiếu (Loki)', bod: 'Max Vũ & Vy Phan', note: 'Khối Truyền thông & Quảng cáo (BUM auto-duyệt trong quỹ phép năm)' },
  { dept: 'Kế toán', leader: 'Nguyễn Thị Thu Xuân', bod: 'Max Vũ & Vy Phan', note: 'Tài chính & Quản lý quỹ phép (BUM auto-duyệt trong quỹ phép năm)' }
];

const FAQS = [
  {
    q: 'Ban Giám Đốc (BOD: Sếp Max Vũ & Sếp Vy Phan) làm đơn xin nghỉ có cần ai duyệt không?',
    a: 'KHÔNG. Sếp Max Vũ và Sếp Vy Phan là 2 thành viên Ban Giám Đốc nắm thẩm quyền điều hành cao nhất tại FIT Tour. Bất kỳ đơn xin nghỉ phép nào do Sếp Max Vũ hoặc Sếp Vy Phan tự tạo trên hệ thống đều được TỰ ĐỘNG DUYỆT (Auto-Approved) 100% ngay lập tức, không bắt duyệt chéo, đồng thời hệ thống tự động bắn email lịch vắng mặt đến Toàn bộ Nhân viên (ALL_STAFF).'
  },
  {
    q: 'Trưởng bộ phận / BUM (Thịnh BU1, Tuấn BU2, Trang BU4, Như BU5, Loki MKT, Xuân KT) xin nghỉ thì ai duyệt?',
    a: 'Phân 2 trường hợp: (1) Nếu số ngày xin nghỉ nằm trong quỹ phép năm còn lại: Hệ thống sẽ TỰ ĐỘNG DUYỆT (Auto-Approved) ngay lập tức để tối ưu hóa thời gian vận hành. (2) Nếu số ngày xin nghỉ vượt quá quỹ phép năm (Âm phép): Đơn sẽ được chuyển trực tiếp cho Ban Giám Đốc (Sếp Max Vũ & Sếp Vy Phan) thẩm định và phê duyệt.'
  },
  {
    q: 'Ai là người có thẩm quyền phê duyệt đơn nghỉ phép của nhân viên thông thường?',
    a: 'Theo quy định ERP 2026, đơn xin nghỉ của nhân viên (trừ đi công tác) bắt buộc qua 2 cấp: Trưởng phòng/BUM trực tiếp quản lý bộ phận của bạn (BU1: Quốc Thịnh, BU2 & Visa: Minh Tuấn, BU3: Vy Phan, BU4: Hồng Trang, BU5: Quỳnh Như, Marketing: Loki, Kế toán: Nguyễn Xuân) VÀ Ban Giám Đốc (BOD: Sếp Max Vũ & Sếp Vy Phan). BUM hoặc BOD đều có thẩm quyền xem xét và duyệt đơn.'
  },
  {
    q: 'Đi công tác hoặc được phân công công việc có cần chờ BUM/BOD duyệt không?',
    a: 'KHÔNG. Loại đơn "Đi công tác / phân công theo công việc" là hoạt động nghiệp vụ được công ty chỉ định nên hệ thống sẽ TỰ ĐỘNG DUYỆT (Auto-Approved) ngay khi bấm gửi. Đồng thời, hệ thống sẽ tự động gửi email thông báo lịch trình công tác đến Toàn bộ Nhân viên (ALL_STAFF) để công ty nắm lịch phối hợp.'
  },
  {
    q: 'Nếu tôi xin nghỉ vượt quá số ngày phép năm còn lại (Âm phép) thì đơn có bị hủy không?',
    a: 'KHÔNG bị hủy. Hệ thống vẫn cho phép bạn gửi đơn, tuy nhiên trên giao diện và trong email gửi BUM/BOD sẽ hiện cảnh báo đỏ số ngày bị Âm phép. Khi BUM/BOD duyệt đơn, phần ngày vượt phép sẽ được hệ thống gắn nhãn rõ ràng để Kế toán (Nguyễn Xuân) đối soát và tính khấu trừ vào kỳ lương tháng đó.'
  },
  {
    q: 'Tại sao email thông báo gửi Toàn bộ Nhân viên (ALL_STAFF) lại hiển thị đầy đủ lý do nghỉ?',
    a: 'Để đảm bảo tính minh bạch và giúp các bộ phận liên quan (Sales, Điều hành, Kế toán, HDV) nắm bắt đúng tính chất vắng mặt nhằm chủ động sắp xếp công việc thay thế hoặc hỗ trợ khẩn cấp khi cần thiết.'
  },
  {
    q: 'Quy định 12 ngày phép năm và cách tính cho nhân viên mới vào làm giữa năm?',
    a: 'Mỗi năm làm việc trọn vẹn, nhân viên chính thức được hưởng 12 ngày phép năm có lương (1 tháng tích lũy 1 ngày). Nếu bạn là nhân viên mới gia nhập công ty vào tháng đột xuất giữa năm, số ngày phép năm đầu tiên sẽ tính tỷ lệ theo số tháng làm việc thực tế còn lại trong năm đó (ví dụ vào làm ngày 01/05 -> còn 8 tháng trong năm 2026 -> quỹ phép là 8 ngày).'
  },
  {
    q: 'Thời gian thử việc có được tính phép năm và sử dụng ngay không?',
    a: 'Trong thời gian 2 tháng thử việc, nhân sự chưa được sử dụng phép năm. Tuy nhiên ngay khi ký Hợp đồng lao động chính thức, 2 tháng thử việc này sẽ được tính gộp hồi tố vào quỹ phép năm của bạn (ví dụ thử việc xong ký HĐLĐ là bạn đã có sẵn 2 ngày phép tích lũy).'
  },
  {
    q: 'Nếu vào làm giữa tháng (sau ngày 15) thì tháng đó có được tính phép không?',
    a: 'Theo quy chế nhân sự: Nếu bạn vào làm từ ngày 01 đến ngày 15 trong tháng, tháng đó được tính trọn 1 ngày phép. Nếu vào làm sau ngày 15, việc tích lũy phép năm sẽ bắt đầu tính từ tháng kế tiếp.'
  },
  {
    q: 'Tôi có thể xin nghỉ phép nửa ngày (0.5 ngày) được không?',
    a: 'Hoàn toàn được! Sau khi bạn bấm chọn ngày trên lịch, tại danh sách ngày đã chọn, bạn có thể chọn phân bổ buổi "Cả ngày", "Buổi sáng" hoặc "Buổi chiều". Hệ thống sẽ tự động tính 0.5 ngày cho mỗi buổi sáng hoặc chiều.'
  },
  {
    q: 'Hệ thống có tự động trừ ngày Thứ 7 và Chủ Nhật không?',
    a: 'Có. Lịch chọn ngày trên ERP FIT Tour được tích hợp thuật toán tự động khóa các ngày Thứ 7 và Chủ Nhật. Nếu bạn bấm vào cuối tuần, hệ thống sẽ cảnh báo và không tính vào tổng số ngày nghỉ.'
  },
  {
    q: 'Phép năm của năm cũ chưa dùng hết có được chuyển sang năm sau không?',
    a: 'Có, nhân sự được hỗ trợ gia hạn sử dụng phép năm cũ đến hết ngày 31/03 của năm kế tiếp. Sau ngày 31/03, hệ thống sẽ chốt và reset số phép cũ chưa dùng về 0.'
  },
  {
    q: 'Sau khi gửi đơn, tôi có thể xem lại hoặc hủy đơn được không?',
    a: 'Được. Tại góc trên bên phải của modal tạo đơn, bạn có nút "Quản lý nghỉ phép" (hoặc truy cập mục Quản lý nghỉ phép trong CRM) để theo dõi trạng thái đơn (Chờ duyệt / Đã duyệt / Từ chối) và gửi yêu cầu hủy nếu kế hoạch thay đổi.'
  }
];

import ErpGuideLayout from '../components/ErpGuideLayout';

export default function HuongDanTaoNghiPhepPage() {
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxTitle, setLightboxTitle] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [isTableExpanded, setIsTableExpanded] = useState(false);

  const activeStep = STEPS_DATA[currentStepIndex];

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowRight') {
      setCurrentStepIndex((prev) => Math.min(prev + 1, STEPS_DATA.length - 1));
    } else if (e.key === 'ArrowLeft') {
      setCurrentStepIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Escape') {
      setLightboxImage(null);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const openLightbox = (imgSrc, title) => {
    setLightboxImage(imgSrc);
    setLightboxTitle(title);
  };

  return (
    <ErpGuideLayout currentGuideId="tao-nghi-phep">
      {/* ── Hero Section ── */}
      <header className="erp-guide-hero" style={{ paddingBottom: '24px' }}>
        <div className="erp-hero-inner">
          <div className="erp-breadcrumbs">
            <Link to="/">Trang chủ CRM</Link>
            <span>/</span>
            <Link to="/huong-dan-erp">Hướng dẫn ERP</Link>
            <span>/</span>
            <span className="active">Tạo đơn xin nghỉ phép</span>
          </div>

          <div className="erp-hero-header-row">
            <div className="erp-hero-title-group">
              <h1>Hướng Dẫn Sử Dụng ERP: Tạo Đơn Xin Nghỉ Phép</h1>
              <p className="erp-hero-desc">
                Cẩm nang chuẩn hóa thao tác gửi đơn xin nghỉ phép trên hệ thống ERP FIT Tour dành cho toàn thể Cán bộ – Nhân viên. Tự động kiểm tra số ngày phép dư, loại trừ cuối tuần, bàn giao công việc và thông báo qua Email.
              </p>

              <div className="erp-hero-meta-tags">
                <span className="erp-meta-tag">
                  <CheckCircle size={14} color="#10b981" /> Phiên bản ERP 2026
                </span>
                <span className="erp-meta-tag">
                  <Clock size={14} color="#3b82f6" /> 2 phút thực hiện
                </span>
                <span className="erp-meta-tag">
                  <Users size={14} color="#8b5cf6" /> Toàn thể CBNV
                </span>
                <span className="erp-meta-tag">
                  <ShieldCheck size={14} color="#f59e0b" /> Quy định nội bộ FIT Tour
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ── Main Content Container ── */}
      <main className="erp-guide-main">
        {/* ── SECTION 0: VISUAL FLOWCHART WORKFLOW ── */}
        <section className="erp-section-block" style={{ marginBottom: '32px' }} id="workflow-flowchart">
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0',
            borderRadius: '20px',
            padding: '28px 24px',
            boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#eff6ff', color: '#2563eb', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, marginBottom: '8px' }}>
                  <GitBranch size={13} /> SƠ ĐỒ QUY TRÌNH 2026 • CHUẨN ERP FIT TOUR
                </div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>
                  Sơ Đồ Luồng Phê Duyệt & Phân Luồng Thông Báo Nghỉ Phép
                </h2>
                <p style={{ margin: '6px 0 0', fontSize: '13.5px', color: '#64748b', maxWidth: '780px', lineHeight: '1.6' }}>
                  Mô hình thẩm định 2 cấp (Trưởng phòng/BUM + Ban Giám Đốc), cơ chế <strong>Auto-Duyệt Đi công tác</strong>, xử lý <strong>Âm phép</strong> đối soát cho Kế toán và tự động bắn Email toàn công ty kèm đầy đủ lý do.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#ecfdf5', color: '#059669', padding: '6px 12px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 600 }}>
                  <CheckCircle size={14} /> Auto-Duyệt Công Tác
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#f5f3ff', color: '#7c3aed', padding: '6px 12px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 600 }}>
                  <ShieldCheck size={14} /> BUM & BOD Thẩm Định
                </span>
              </div>
            </div>

            {/* FLOWCHART GRAPHIC IMAGE BANNER */}
            <div 
              style={{ 
                marginBottom: '24px', 
                borderRadius: '16px', 
                overflow: 'hidden', 
                border: '1.5px solid #cbd5e1', 
                boxShadow: '0 4px 16px rgba(15, 23, 42, 0.06)', 
                position: 'relative', 
                cursor: 'pointer',
                background: '#f8fafc'
              }} 
              onClick={() => openLightbox('/images/huong-dan/tao-nghi-phep/flowchart-duyet-nghi-phep-2026.svg', 'Sơ đồ quy trình phê duyệt & phân luồng nghỉ phép 2026')}
              title="Nhấp để phóng to toàn màn hình"
            >
              <img 
                src="/images/huong-dan/tao-nghi-phep/flowchart-duyet-nghi-phep-2026.svg" 
                alt="Sơ đồ quy trình phê duyệt & phân luồng nghỉ phép FIT Tour 2026" 
                style={{ width: '100%', height: 'auto', display: 'block' }} 
              />
              <div style={{ 
                position: 'absolute', 
                bottom: '16px', 
                right: '16px', 
                background: 'rgba(15, 23, 42, 0.85)', 
                color: 'white', 
                padding: '8px 16px', 
                borderRadius: '24px', 
                fontSize: '12.5px', 
                fontWeight: 600, 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                backdropFilter: 'blur(6px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}>
                <Maximize2 size={14} /> Phóng to sơ đồ luồng (Full HD)
              </div>
            </div>

            {/* FLOWCHART GRAPHIC CANVAS */}
            <div style={{
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              borderRadius: '16px',
              padding: '24px 20px',
              position: 'relative',
              overflowX: 'auto'
            }}>
              {/* STAGE 1: CREATION */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '24px' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                  color: 'white',
                  borderRadius: '12px',
                  padding: '14px 24px',
                  width: '100%',
                  maxWidth: '520px',
                  boxShadow: '0 4px 14px rgba(15, 23, 42, 0.15)',
                  textAlign: 'center'
                }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.15)', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700, marginBottom: '6px' }}>
                    BƯỚC 1: KHỞI TẠO ĐƠN TRÊN ERP
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: 700 }}>Nhân viên tạo Đơn Xin Nghỉ Phép</div>
                  <div style={{ fontSize: '12px', color: '#cbd5e1', marginTop: '4px' }}>
                    Chọn loại nghỉ • Chọn ngày trên lịch (tự khóa T7/CN) • Người bàn giao • Nhập lý do đầy đủ
                  </div>
                </div>

                <div style={{ width: '2px', height: '24px', background: '#94a3b8', margin: '4px 0' }} />
                <div style={{ fontSize: '18px', color: '#64748b', lineHeight: 1 }}>▼</div>
              </div>

              {/* STAGE 2: BRANCHING */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '24px' }}>
                
                {/* BRANCH A: AUTO-DUYỆT (CÔNG TÁC, BOD, BUM TRONG PHÉP) */}
                <div style={{
                  background: '#f0fdf4',
                  border: '1.5px solid #86efac',
                  borderRadius: '14px',
                  padding: '18px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  position: 'relative'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ background: '#10b981', color: 'white', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 800 }}>
                      NHÁNH 1 • AUTO-DUYỆT TỰ ĐỘNG
                    </span>
                    <span style={{ fontSize: '12px', color: '#047857', fontWeight: 700 }}>
                      ⚡ Auto-Duyệt 100%
                    </span>
                  </div>

                  <div>
                    <h4 style={{ margin: '0 0 4px', fontSize: '15px', color: '#065f46', fontWeight: 700 }}>
                      Công Tác • Ban Giám Đốc • BUM Trong Quỹ Phép
                    </h4>
                    <p style={{ margin: 0, fontSize: '12.5px', color: '#047857', lineHeight: '1.5' }}>
                      1. Đi công tác nghiệp vụ theo phân công.<br/>
                      2. Ban Giám Đốc (Max Vũ & Vy Phan) tự làm đơn nghỉ phép.<br/>
                      3. Trưởng bộ phận / BUM nghỉ phép đúng trong quỹ phép năm còn lại.
                    </p>
                  </div>

                  <div style={{ background: 'white', borderRadius: '8px', padding: '12px', border: '1px solid #bbf7d0', fontSize: '12px', color: '#166534', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
                      <Mail size={14} color="#10b981" /> 1. Gửi Email Xác Nhận đến Người Làm Đơn
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
                      <Users size={14} color="#10b981" /> 2. Bắn Email Lịch Vắng Mặt đến Toàn Thể Nhân Viên (ALL_STAFF)
                    </div>
                    <div style={{ fontSize: '11px', color: '#15803d', fontStyle: 'italic', marginTop: '2px' }}>
                      * Trừ quỹ phép tự động (nếu là phép năm). Toàn công ty chủ động nắm lịch phối hợp.
                    </div>
                  </div>
                </div>

                {/* BRANCH B: THẨM ĐỊNH (NHÂN VIÊN / BUM ÂM PHÉP) */}
                <div style={{
                  background: '#f8fafc',
                  border: '1.5px solid #93c5fd',
                  borderRadius: '14px',
                  padding: '18px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  position: 'relative'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ background: '#2563eb', color: 'white', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 800 }}>
                      NHÁNH 2 • CHỜ THẨM ĐỊNH & PHÊ DUYỆT
                    </span>
                    <span style={{ fontSize: '12px', color: '#1d4ed8', fontWeight: 700 }}>
                      ⏳ Chờ Duyệt (Pending)
                    </span>
                  </div>

                  <div>
                    <h4 style={{ margin: '0 0 4px', fontSize: '15px', color: '#1e3a8a', fontWeight: 700 }}>
                      Nhân Viên Phép Năm/Việc Riêng • BUM Vượt Quỹ Phép
                    </h4>
                    <p style={{ margin: 0, fontSize: '12.5px', color: '#334155', lineHeight: '1.5' }}>
                      • <strong>Nhân viên:</strong> Bắt buộc thẩm định từ <strong>Trưởng phòng/BUM</strong> VÀ <strong>Ban Giám Đốc (Max Vũ & Vy Phan)</strong>.<br/>
                      • <strong>BUM vượt quỹ phép (Âm phép):</strong> Chuyển trực tiếp đến <strong>Ban Giám Đốc (Max Vũ & Vy Phan)</strong> duyệt.
                    </p>
                  </div>

                  {/* OVERDRAFT NOTICE BOX */}
                  <div style={{ background: '#fffbeb', borderRadius: '8px', padding: '10px 12px', border: '1px solid #fde68a', fontSize: '11.5px', color: '#92400e' }}>
                    <strong>⚠️ Đối soát Âm phép:</strong> Nếu số ngày xin vượt quá quỹ phép dư, hệ thống vẫn cho phép gửi đơn nhưng sẽ gắn cờ cảnh báo số ngày âm phép để BUM/BOD xem xét duyệt và Kế toán đối soát trừ lương.
                  </div>

                  <div style={{ background: 'white', borderRadius: '8px', padding: '12px', border: '1px solid #bfdbfe', fontSize: '12px', color: '#1e40af', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Mail size={14} color="#2563eb" /> Email Thẩm Định Gửi Trực Tiếp Đến BUM & BOD
                    </div>
                    <div style={{ fontSize: '11.5px', color: '#475569' }}>
                      Bao gồm: Lịch nghỉ • Quỹ phép còn lại • Người bàn giao • Nút Duyệt / Từ chối 1-chạm
                    </div>
                  </div>
                </div>

              </div>

              {/* STAGE 3: APPROVAL DECISION RESULT */}
              <div style={{
                background: '#f1f5f9',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid #cbd5e1'
              }}>
                <div style={{ textAlign: 'center', marginBottom: '14px' }}>
                  <span style={{ background: '#475569', color: 'white', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700 }}>
                    BƯỚC 3: QUYẾT ĐỊNH PHÊ DUYỆT CỦA BUM / BOD
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
                  {/* CASE 1: APPROVED */}
                  <div style={{ background: 'white', borderRadius: '10px', padding: '14px', border: '1px solid #86efac', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#166534', fontWeight: 800, fontSize: '13.5px' }}>
                      <CheckCircle size={16} color="#16a34a" /> TRƯỜNG HỢP 1: XÁC NHẬN DUYỆT (APPROVED)
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#334155', lineHeight: '1.6' }}>
                      <li><strong>Gửi email xác nhận:</strong> Thông báo đơn đã được duyệt gửi trực tiếp cho nhân viên.</li>
                      <li><strong>Bắn email Toàn Bộ Nhân Viên (ALL_STAFF):</strong> Thông báo công khai lịch nghỉ, người nhận bàn giao, SĐT liên hệ và <u>HIỂN THỊ ĐẦY ĐỦ LÝ DO NGHỈ</u>.</li>
                      <li><strong>Trừ quỹ phép:</strong> Hệ thống tự động trừ ngày nghỉ vào quỹ phép năm. Nếu âm phép, số ngày vượt sẽ được gắn cờ chuyển Kế toán tính lương.</li>
                    </ul>
                  </div>

                  {/* CASE 2: REJECTED */}
                  <div style={{ background: 'white', borderRadius: '10px', padding: '14px', border: '1px solid #fca5a5', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#991b1b', fontWeight: 800, fontSize: '13.5px' }}>
                      <XCircle size={16} color="#dc2626" /> TRƯỜNG HỢP 2: TỪ CHỐI (REJECTED)
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#334155', lineHeight: '1.6' }}>
                      <li><strong>Gửi lý do qua email cho nhân viên:</strong> BUM/BOD nhập lý do không duyệt, hệ thống tự động gửi email thông báo kèm lý do đến nhân viên.</li>
                      <li><strong>Không gửi toàn công ty:</strong> Đơn bị từ chối được đóng lại, không phát tán email đến ALL_STAFF.</li>
                      <li><strong>Giữ nguyên quỹ phép:</strong> Số ngày phép năm của nhân viên không bị trừ.</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* STAGE 4: APPROVER MATRIX TABLE */}
              <div style={{ marginTop: '20px', background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ShieldCheck size={18} color="#2563eb" />
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 800, color: '#0f172a' }}>
                      Ma Trận Người Duyệt Phép Theo Phòng Ban (BUM & BOD Matrix)
                    </h4>
                  </div>
                  <span style={{ fontSize: '11.5px', color: '#64748b', background: '#f8fafc', padding: '3px 8px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                    BOD (Sếp Max Vũ & Sếp Vy Phan) luôn có thẩm quyền cao nhất trên mọi đơn
                  </span>
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0', color: '#475569' }}>
                        <th style={{ padding: '8px 12px' }}>Bộ Phận / BU</th>
                        <th style={{ padding: '8px 12px' }}>Trưởng Phòng / BUM Trực Tiếp</th>
                        <th style={{ padding: '8px 12px' }}>Ban Giám Đốc (BOD)</th>
                        <th style={{ padding: '8px 12px' }}>Ghi Chú Vận Hành</th>
                      </tr>
                    </thead>
                    <tbody>
                      {APPROVER_MATRIX.map((m, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '8px 12px', fontWeight: 700, color: '#1e293b' }}>{m.dept}</td>
                          <td style={{ padding: '8px 12px', color: '#2563eb', fontWeight: 600 }}>{m.leader}</td>
                          <td style={{ padding: '8px 12px', color: '#059669', fontWeight: 600 }}>{m.bod}</td>
                          <td style={{ padding: '8px 12px', color: '#64748b' }}>{m.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ── SECTION 1: INTERACTIVE SLIDER ── */}
        <section className="erp-slider-section" id="slider-section">
          {/* Stepper Tab Bar */}
          <div className="erp-slider-stepper">
            {STEPS_DATA.map((st, idx) => {
              const isActive = idx === currentStepIndex;
              return (
                <button
                  key={st.step}
                  className={`erp-step-tab ${isActive ? 'active' : ''}`}
                  onClick={() => setCurrentStepIndex(idx)}
                >
                  <div className="erp-step-tab-badge">{st.step}</div>
                  <div className="erp-step-tab-info">
                    <h4>{st.shortTitle}</h4>
                    <p>{st.badgeText}</p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Slide Content Grid */}
          <div className="erp-slide-body">
            {/* Left Column: Detailed Instructions */}
            <div className="erp-slide-instruction">
              <div>
                <span className="erp-slide-step-badge">
                  <Sparkles size={13} /> {activeStep.badgeText}
                </span>
                <h3 className="erp-slide-title">{activeStep.title}</h3>
                <p className="erp-slide-desc">{activeStep.desc}</p>

                {/* Step Actions Checklist */}
                <ul className="erp-step-action-list">
                  {activeStep.actions.map((act, actIdx) => (
                    <li key={actIdx} className="erp-step-action-item">
                      <div className="erp-item-icon">{act.icon}</div>
                      <div>
                        <strong style={{ color: '#0f172a' }}>{act.title}: </strong>
                        <span>{act.detail}</span>
                      </div>
                    </li>
                  ))}
                </ul>

                {/* Callout Tip Box */}
                <div className="erp-slider-tip">
                  <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <strong>Mẹo / Lưu ý: </strong>
                    <span>{activeStep.tip}</span>
                  </div>
                </div>
              </div>

              {/* Slider Bottom Controls */}
              <div className="erp-slider-controls">
                <div className="erp-slider-dots">
                  {STEPS_DATA.map((_, dotIdx) => (
                    <button
                      key={dotIdx}
                      className={`erp-dot ${dotIdx === currentStepIndex ? 'active' : ''}`}
                      onClick={() => setCurrentStepIndex(dotIdx)}
                      title={`Đến bước ${dotIdx + 1}`}
                    />
                  ))}
                  <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px' }}>
                    Bước {currentStepIndex + 1} / {STEPS_DATA.length}
                  </span>
                </div>

                <div className="erp-slider-btn-group">
                  <button
                    className="erp-ctrl-btn"
                    onClick={() => setCurrentStepIndex((prev) => Math.max(prev - 1, 0))}
                    disabled={currentStepIndex === 0}
                  >
                    <ChevronLeft size={16} /> Quay lại
                  </button>

                  {currentStepIndex < STEPS_DATA.length - 1 ? (
                    <button
                      className="erp-ctrl-btn primary-ctrl"
                      onClick={() => setCurrentStepIndex((prev) => Math.min(prev + 1, STEPS_DATA.length - 1))}
                    >
                      Bước tiếp theo <ChevronRight size={16} />
                    </button>
                  ) : (
                    <button
                      className="erp-ctrl-btn primary-ctrl"
                      onClick={() => {
                        toast.success('Bạn đã xem hết các bước hướng dẫn!');
                        setIsTableExpanded(true);
                        const el = document.getElementById('field-reference');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      Xem chi tiết các trường <Check size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column: Visual Preview with Mockup Frame */}
            <div className="erp-slide-visual">
              <div className="erp-mockup-frame">
                <div className="erp-mockup-header">
                  <div className="erp-mockup-dots">
                    <span className="mockup-dot red"></span>
                    <span className="mockup-dot yellow"></span>
                    <span className="mockup-dot green"></span>
                  </div>
                  <div className="erp-mockup-url">
                    https://erp.fittour.vn
                  </div>
                </div>

                <div 
                  className="erp-mockup-body"
                  onClick={() => openLightbox(activeStep.image, activeStep.title)}
                  title="Nhấp để phóng to ảnh xem chi tiết"
                >
                  <img src={activeStep.image} alt={activeStep.title} />
                  <div className="erp-mockup-zoom-overlay">
                    <div className="erp-zoom-pill">
                      <Maximize2 size={14} /> Bấm để phóng to xem chi tiết
                    </div>
                  </div>
                </div>
              </div>

              <div className="erp-mockup-caption">
                <Maximize2 size={13} /> {activeStep.caption}
              </div>
            </div>
          </div>
        </section>

        {/* ── SECTION 2: FIELD REFERENCE TABLE (FOLDABLE / UNFOLDABLE) ── */}
        <section className="erp-section-block" id="field-reference">
          <div className="erp-foldable-card">
            <button
              type="button"
              className="erp-fold-header"
              onClick={() => setIsTableExpanded(!isTableExpanded)}
              title={isTableExpanded ? 'Nhấn để thu gọn bảng' : 'Nhấn để mở rộng bảng'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div className="erp-fold-icon">
                  <FileText size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                    Bảng Giải Thích Chi Tiết 9 Trường Thông Tin Trong Đơn
                  </h3>
                  <p style={{ margin: '3px 0 0', fontSize: '13px', color: '#64748b' }}>
                    {isTableExpanded 
                      ? 'Nhấn vào đây để thu gọn bảng' 
                      : 'Bao gồm: Nhân viên xin phép, Người duyệt, Loại phép, Ngày nghỉ, SĐT, Lý do, Bàn giao... (Nhấn để xem chi tiết)'}
                  </p>
                </div>
              </div>
              <div className="erp-fold-status">
                <span className="erp-fold-badge">
                  {isTableExpanded ? 'Thu gọn bảng' : 'Xem 9 trường thông tin'}
                </span>
                {isTableExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </button>

            {isTableExpanded && (
              <div className="erp-fold-body">
                <div className="erp-table-responsive">
                  <table className="erp-table">
                    <thead>
                      <tr>
                        <th style={{ width: '22%' }}>Trường Thông Tin</th>
                        <th style={{ width: '15%' }}>Định Dạng</th>
                        <th style={{ width: '12%' }}>Bắt Buộc?</th>
                        <th style={{ width: '51%' }}>Mô Tả Chi Tiết & Lưu Ý Nghiệp Vụ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {DATA_FIELDS.map((f, i) => (
                        <tr key={i}>
                          <td>
                            <span className="erp-field-name">{f.name}</span>
                          </td>
                          <td>
                            <code style={{ fontSize: '12px', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                              {f.type}
                            </code>
                          </td>
                          <td>
                            {f.required ? (
                              <span style={{ color: '#ef4444', fontWeight: 'bold', fontSize: '12px' }}>
                                ● Bắt buộc
                              </span>
                            ) : (
                              <span className="badge-optional">Tùy chọn</span>
                            )}
                          </td>
                          <td>{f.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ── SECTION 3: LEAVE POLICY & CALCULATION FOR NEW EMPLOYEES ── */}
        <section className="erp-section-block">
          <div className="erp-section-title-wrap">
            <div className="erp-section-icon" style={{ background: '#ecfdf5', color: '#059669' }}>
              <Calculator size={20} />
            </div>
            <div>
              <h2>Chính Sách Phép Năm & Quy Tắc Tính Cho Nhân Sự Mới</h2>
              <p>Quy định 12 ngày phép/năm và công thức tính ngày phép khi vào làm việc giữa năm (tháng đột xuất)</p>
            </div>
          </div>

          <div className="erp-policy-special-box">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <span className="erp-formula-tag">Quy tắc chuẩn</span>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#0f172a' }}>
                1 Năm Có 12 Ngày Nghỉ Phép • Mỗi Tháng Tích Lũy 1 Ngày
              </h3>
            </div>
            
            <p style={{ margin: '0 0 14px 0', fontSize: '14px', color: '#334155', lineHeight: '1.6' }}>
              Theo Bộ luật Lao động và Quy chế nhân sự FIT Tour, nhân sự làm việc đủ 12 tháng tại công ty sẽ có quỹ <strong>12 ngày phép năm</strong> hưởng nguyên 100% lương. Hệ thống ERP tính tích lũy đều đặn theo tỷ lệ: <strong>Cứ làm việc đủ 1 tháng thực tế = tích lũy được 1 ngày phép năm</strong>.
            </p>

            <div className="erp-policy-formula-bar">
              <span className="erp-formula-tag" style={{ background: '#059669' }}>Công thức Nhân Viên Mới</span>
              <div className="erp-formula-text">
                Quỹ phép năm đầu tiên = Số tháng làm việc thực tế còn lại trong năm (tính từ tháng vào làm đến hết tháng 12)
              </div>
            </div>

            <div style={{ fontSize: '13.5px', color: '#475569', margin: '14px 0 6px 0', lineHeight: '1.6' }}>
              <strong>📌 Quy tắc xác định ngày vào làm trong tháng:</strong>
              <ul style={{ margin: '6px 0 14px 20px', padding: 0 }}>
                <li><strong>Vào làm từ ngày 01 đến ngày 15 trong tháng:</strong> Được tính trọn vẹn <strong>1 ngày phép</strong> cho tháng đó.</li>
                <li><strong>Vào làm sau ngày 15 trong tháng:</strong> Bắt đầu tính tích lũy phép từ tháng kế tiếp.</li>
                <li><strong>Thời gian thử việc:</strong> Trong 2 tháng thử việc, nhân sự chưa được sử dụng phép năm. Sau khi ký Hợp đồng lao động chính thức, số tháng thử việc sẽ được <em>tính gộp hồi tố</em> vào quỹ phép năm để bạn sử dụng.</li>
              </ul>
            </div>

            <h4 style={{ margin: '18px 0 10px 0', fontSize: '14px', fontWeight: 800, color: '#1e293b' }}>
              💡 Các ví dụ tính phép thực tế cho nhân sự mới:
            </h4>

            <div className="erp-examples-grid">
              <div className="erp-example-card">
                <div className="erp-example-header">
                  <span>🌱 Vào làm ngày 05/04</span>
                  <span className="erp-example-badge">Trước ngày 15</span>
                </div>
                <div className="erp-example-result">
                  Tính từ Tháng 4 đến Tháng 12 = <strong>9 tháng</strong>.<br />
                  Quỹ phép năm đầu tiên: <strong>9 ngày phép</strong>.
                </div>
              </div>

              <div className="erp-example-card">
                <div className="erp-example-header">
                  <span>🍁 Vào làm ngày 01/07</span>
                  <span className="erp-example-badge">Giữa năm</span>
                </div>
                <div className="erp-example-result">
                  Tính từ Tháng 7 đến Tháng 12 = <strong>6 tháng</strong>.<br />
                  Quỹ phép năm đầu tiên: <strong>6 ngày phép</strong>.
                </div>
              </div>

              <div className="erp-example-card">
                <div className="erp-example-header">
                  <span>❄️ Vào làm ngày 22/10</span>
                  <span className="erp-example-badge">Sau ngày 15</span>
                </div>
                <div className="erp-example-result">
                  Không tính T10, tính T11 và T12 = <strong>2 tháng</strong>.<br />
                  Quỹ phép năm đầu tiên: <strong>2 ngày phép</strong>.
                </div>
              </div>
            </div>
          </div>

          <div className="erp-notice-grid">
            <div className="erp-notice-box">
              <div className="erp-notice-icon" style={{ background: '#ecfdf5', color: '#059669' }}>
                🌴
              </div>
              <div className="erp-notice-content">
                <h4>Nghỉ Vượt Quá Số Phép Tích Lũy</h4>
                <p>
                  Nếu bạn xin nghỉ nhiều hơn số ngày phép đã tích lũy tính đến thời điểm hiện tại, phần ngày nghỉ vượt trội sẽ tự động tính sang <strong>Nghỉ không hưởng lương</strong> và trừ vào kỳ lương tháng đó.
                </p>
              </div>
            </div>

            <div className="erp-notice-box">
              <div className="erp-notice-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
                📅
              </div>
              <div className="erp-notice-content">
                <h4>Hạn Sử Dụng & Chuyển Phép Năm</h4>
                <p>
                  Toàn bộ phép năm được khuyến khích sử dụng trong năm. Số phép năm chưa dùng hết được hỗ trợ gia hạn sử dụng đến hết ngày <strong>31/03 của năm kế tiếp</strong> (sau ngày 31/03, số phép cũ chưa dùng sẽ tự động hủy).
                </p>
              </div>
            </div>

            <div className="erp-notice-box">
              <div className="erp-notice-icon" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                🛡️
              </div>
              <div className="erp-notice-content">
                <h4>Tự Động Loại Trừ Cuối Tuần (T7 & CN)</h4>
                <p>
                  Hệ thống thông minh tự động loại trừ Thứ 7 và Chủ Nhật khi bạn bấm chọn khoảng ngày trên lịch. Số ngày nghỉ chỉ tính các ngày làm việc thực tế (Thứ 2 đến Thứ 6), đảm bảo không bao giờ bị trừ oan phép.
                </p>
              </div>
            </div>

            <div className="erp-notice-box">
              <div className="erp-notice-icon" style={{ background: '#fff7ed', color: '#ea580c' }}>
                ✉️
              </div>
              <div className="erp-notice-content">
                <h4>Bàn Giao & Thông Báo Tự Động Đến BGĐ</h4>
                <p>
                  Bắt buộc chọn người nhận bàn giao công việc và ghi chú task cần hỗ trợ. Ngay khi bấm gửi, ERP tự động gửi Email thông báo đến Ban Giám Đốc và đồng đội cùng phòng ban (BU).
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── SECTION 4: FAQS ACCORDION ── */}
        <section className="erp-section-block">
          <div className="erp-section-title-wrap">
            <div className="erp-section-icon" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
              <HelpCircle size={20} />
            </div>
            <div>
              <h2>Câu Hỏi Thường Gặp (FAQ)</h2>
              <p>Giải đáp thắc mắc thường gặp của CBNV khi tạo đơn xin nghỉ phép</p>
            </div>
          </div>

          <div className="erp-faq-list">
            {FAQS.map((faq, fIdx) => {
              const isOpen = openFaqIndex === fIdx;
              return (
                <div key={fIdx} className="erp-faq-item">
                  <button
                    className="erp-faq-question"
                    onClick={() => setOpenFaqIndex(isOpen ? -1 : fIdx)}
                  >
                    <span>{faq.q}</span>
                    <span style={{ color: '#64748b', fontSize: '18px', fontWeight: 'bold' }}>
                      {isOpen ? '−' : '+'}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="erp-faq-answer">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* ── SECTION 5: CTA BANNER ── */}
        <section className="erp-cta-banner">
          <div className="erp-cta-text">
            <h3>Sẵn sàng tạo đơn xin nghỉ phép?</h3>
            <p>
              Hệ thống ERP FIT Tour đã sẵn sàng. Truy cập ngay trang chủ để trải nghiệm luồng tạo đơn nhanh chóng chỉ với 3 bước đơn giản.
            </p>
          </div>
          <div className="erp-cta-actions">
            <Link to="/" className="erp-btn-white">
              Vào CRM FIT Tour Ngay
            </Link>
            <Link to="/huong-dan-erp" className="erp-btn-ghost" style={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}>
              Xem thêm hướng dẫn khác
            </Link>
          </div>
        </section>
      </main>

      {/* ── Lightbox Modal for Image Zoom ── */}
      {lightboxImage && (
        <div className="erp-lightbox-overlay" onClick={() => setLightboxImage(null)}>
          <div className="erp-lightbox-header" onClick={(e) => e.stopPropagation()}>
            <div className="erp-lightbox-title">
              <Maximize2 size={18} /> {lightboxTitle}
            </div>
            <button className="erp-lightbox-close" onClick={() => setLightboxImage(null)}>
              <X size={20} />
            </button>
          </div>
          <div className="erp-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={lightboxImage} alt={lightboxTitle} />
          </div>
        </div>
      )}
    </ErpGuideLayout>
  );
}

